<template>
    <div class="header do-not-print" :class="{ 'header--open': open }">
        <div class="header__start">
            <button
                type="button"
                class="header__icon-button header__sidebar-button"
                aria-label="Open navigation"
                @click="$emit('sidebarButton')"
            >
                <span class="header__options fas fa-bars"></span>
            </button>
            <div class="header__left-container">
                <router-link to="/home" class="header__brand-link" aria-label="Home">
                    <img src="@/assets/logo-negro.png" class="header__brand-logo" alt="" />
                    <img src="@/assets/logo-pay.png" class="header__brand" alt="" />
                </router-link>
            </div>
            <SearchInput class="header__account-selector"/>
            <app-launcher />
        </div>
        <div class="header__filters">
            <ui-select
                v-if="showCountryFilter"
                id="type"
                class="header__country-selector"
                name="type"
                label="Country"
                option-label="name"
                option-value="code"
                chose-option-text="All countries"
                v-model:value="countryFilter"
                :options="countries"
            />
        </div>
        <div class="header__right-container">
            <div class="header__icon-group">
                <div
                    class="header__notification-anchor"
                    v-click-outside="closeNotificationPanel"
                >
                <button
                    type="button"
                    class="header__icon-button"
                    aria-label="Notifications"
                    :aria-expanded="isNotificationPanelVisible"
                    @click="toggleNotificationPanel"
                >
                    <span class="header__options fas fa-bell"></span>
                    <span
                        v-if="notificationUnreadCount > 0"
                        class="header__notification-badge"
                    >{{ notificationBadgeText }}</span>
                </button>
                <dialog
                    v-if="isNotificationPanelVisible"
                    open
                    class="header__notification-dropdown"
                    aria-label="Notifications list"
                    @click.stop
                    @keydown.esc="closeNotificationPanel"
                >
                    <div class="header__notification-dropdown-header">
                        <span class="header__notification-dropdown-title">Notifications</span>
                        <div class="header__notification-dropdown-header-actions">
                            <button
                                v-if="notificationUnreadCount > 0"
                                type="button"
                                class="header__notification-mark-all"
                                aria-label="Mark all as read"
                                @click.stop="markAllNotificationsRead"
                            >
                                <span class="fas fa-check-double" aria-hidden="true"></span>
                            </button>
                            <button
                                type="button"
                                class="header__notification-dropdown-close"
                                aria-label="Close notifications"
                                @click="closeNotificationPanel"
                            >
                                <span class="fas fa-times"></span>
                            </button>
                        </div>
                    </div>
                    <div class="header__notification-list">
                        <div v-if="!previewItems.length" class="header__notification-empty">No notifications yet.</div>
                        <div
                            v-for="row in previewItems"
                            :key="row.id || row._id"
                            class="header__notification-row"
                            :class="{ 'header__notification-row--unread': !row.viewed }"
                        >
                            <button
                                type="button"
                                class="header__notification-row-main"
                                @click="onPreviewNotificationClick(row)"
                            >
                                <div class="header__notification-top-line">
                                    <div class="header__notification-top-left">
                                        <span class="header__notification-actor">{{ actorLabel(row) }}</span>
                                        <span class="header__notification-sep" aria-hidden="true">·</span>
                                        <span class="header__notification-legend">{{ getNotificationLegend(row) }}</span>
                                    </div>
                                    <div class="header__notification-top-right">
                                        <time class="header__notification-time" :datetime="row.created_at">{{
                                            formatNotificationTime(row.created_at)
                                        }}</time>
                                    </div>
                                </div>
                                <p class="header__notification-body">
                                    <template v-for="(part, pi) in previewParts(row)" :key="`${row.id}-${pi}`">
                                        <span v-if="part.type === 'mention'" class="header__notification-mention">{{ '@' + part.value }}</span>
                                        <span v-else>{{ part.value }}</span>
                                    </template>
                                </p>
                            </button>
                            <div v-if="!row.viewed" class="header__notification-mark-read-wrap">
                                <button
                                    type="button"
                                    class="header__notification-mark-read"
                                    aria-label="Mark as read"
                                    @click.stop="markNotificationRead(row)"
                                >
                                    <span class="fas fa-check" aria-hidden="true"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="header__notification-dropdown-footer">
                        <button type="button" class="header__notification-view-all" @click="goToAllNotifications">
                            View all notifications
                        </button>
                    </div>
                </dialog>
            </div>
            </div>
            <span v-if="account" class="header__divider" aria-hidden="true"></span>
            <div v-if="account" class="header__menu">
                <button type="button" class="header__profile-dropdown" @click="toggleProfileMenu">
                    <div class="header__avatar">
                        <ui-avatar ref="avatar" size="xs" :account="account" />
                    </div>
                    <div class="header__profile-meta">
                        <div class="header__profile-name">{{ fullName }}</div>
                        <div class="header__profile-email">{{ account.email }}</div>
                    </div>
                    <span class="header__profile-chevron fas fa-chevron-down" aria-hidden="true"></span>
                </button>
                <div v-if="isProfileMenuVisible" class="profile-menu-overlay" @click="closeProfileMenu" @keydown.esc="closeProfileMenu">
                    <div class="profile-menu" @click.prevent>
                        <div class="profile-menu__account">
                            <div class="profile-menu__name">{{ fullName }}</div>
                            <div class="profile-menu__email">{{ account.email }}</div>
                        </div>
                        <ul>
                            <li>
                                <button type="button" class="profile-menu__options" @click="$router.push('/dashboard/profile')">Your profile</button>
                            </li>
                            <li>
                                <button type="button" class="profile-menu__options profile-menu__options--logout" @click="$emit('logout')">
                                    <span class="fas fa-sign-out-alt" aria-hidden="true"></span>
                                    Log out
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { sanitizeUrl } from '@braintree/sanitize-url';

import UiAvatar from '@/components/ui/Avatar.vue';
import UiSelect from '@/components/ui/Select';
import SearchInput from '@/components/layouts/dashboard/SearchInput.vue';
import AppLauncher from '@/components/layouts/dashboard/AppLauncher.vue';
import { countries } from '@/data';
import { countryFlag } from '@/utils/country-flags.util';
import {
    getNotificationActorLabel,
    getNotificationLegend,
    getNotificationPreviewParts,
} from '@/utils/notificationPreviewParts';

dayjs.extend(relativeTime);

export default {
    name: 'Header',
    components: { UiAvatar, UiSelect, SearchInput, AppLauncher },
    props: {
        open: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['sidebarButton', 'logout', 'toggleSidebar'],
    data() {
        return {
            countries: countries.map(country => ({
                ...country,
                image: countryFlag(country)
            })),
            isProfileMenuVisible: false,
            isNotificationPanelVisible: false,
        };
    },
    computed: {
        ...mapGetters({
            fullName: 'accounts/fullName',
            country: 'globalFilters/country',
        }),
        ...mapState('accounts', ['account']),
        ...mapState('adminNotifications', {
            notificationUnreadCount: 'unreadCount',
            previewItems: 'previewItems',
        }),
        notificationBadgeText() {
            const n = this.notificationUnreadCount;
            if (n > 99) {
                return '99+';
            }
            return String(n);
        },
        showCountryFilter() {
            const routeName = this.$route.name;
            return routeName === 'accounts' || routeName === 'global-accounts';
        },
        countryFilter: {
            get: function () {
                return this.country;
            },
            set: function (newValue) {
                this.setCountry(newValue || '');
            },
        },
    },
    methods: {
        ...mapActions({ setCountry: 'globalFilters/setCountry' }),
        async toggleNotificationPanel() {
            const next = !this.isNotificationPanelVisible;
            if (next) {
                this.isProfileMenuVisible = false;
                try {
                    await this.$store.dispatch('adminNotifications/refresh');
                } catch (e) {
                    console.error(e);
                }
            }
            this.isNotificationPanelVisible = next;
        },
        closeNotificationPanel() {
            this.isNotificationPanelVisible = false;
        },
        getNotificationLegend,
        actorLabel: getNotificationActorLabel,
        async markNotificationRead(row) {
            if (!row || row.viewed) {
                return;
            }
            try {
                await this.$store.dispatch('adminNotifications/markOneViewed', row.id);
            } catch (e) {
                this.$toast.error(this.getErrorMessage(e) || 'Could not update notification');
            }
        },
        async markAllNotificationsRead() {
            try {
                await this.$store.dispatch('adminNotifications/markAllViewed');
            } catch (e) {
                this.$toast.error(this.getErrorMessage(e) || 'Could not mark notifications');
            }
        },
        previewParts(row) {
            return getNotificationPreviewParts(row.comment_preview, row.comment_mention_labels);
        },
        formatNotificationTime(value) {
            if (!value) {
                return '';
            }
            return dayjs(value).fromNow();
        },
        async onPreviewNotificationClick(row) {
            if (!row.viewed) {
                try {
                    await this.$store.dispatch('adminNotifications/markOneViewed', row.id);
                } catch (e) {
                    this.$toast.error(this.getErrorMessage(e) || 'Could not update notification');
                }
            }
            const raw = row.resource_url || '';
            const safe = sanitizeUrl(raw);
            if (safe && safe !== 'about:blank') {
                globalThis.location.href = safe;
            }
            this.closeNotificationPanel();
        },
        goToAllNotifications() {
            this.closeNotificationPanel();
            this.$router.push({ name: 'dashboard-notifications' });
        },
        toggleProfileMenu() {
            this.isNotificationPanelVisible = false;
            this.isProfileMenuVisible = !this.isProfileMenuVisible;
        },
        closeProfileMenu() {
            this.isProfileMenuVisible = false;
        },
    }
};
</script>

<style lang="scss">
.header {
    align-items: center;
    background: $white;
    border-bottom: 1px solid $ecart-secondary-200;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    display: flex;
    height: 56px;
    left: 0;
    overflow: visible;
    position: fixed;
    right: 0;
    top: 0;
    width: auto;
    z-index: 1030;

    @media(max-width: 992px) {
        gap: 8px;
        padding: 0 12px;
    }

    &--open {
        width: auto;
    }

    &__start {
        align-items: center;
        display: flex;
        flex-shrink: 1;
        gap: 8px;
        min-width: 0;
    }

    &__filters {
        align-items: center;
        display: flex;
        flex: 1;
        gap: 8px;
        justify-content: center;
        min-width: 0;

        .header__country-selector .ui-select__list-container {
            z-index: 1050;
        }

        @media (max-width: 992px) {
            display: none;
            flex: 0 0 0;
            width: 0;
        }
    }

    &__right-container {
        align-items: center;
        align-self: stretch;
        display: flex;
        flex-shrink: 0;
        gap: 8px;
        margin-left: auto;
        position: relative;
    }

    &__icon-group {
        align-items: center;
        display: flex;
        gap: 0;
    }

    &__brand-link {
        align-items: center;
        display: flex;
        min-width: 0;
    }

    &__left-container {
        align-items: center;
        box-sizing: border-box;
        display: flex;
        flex-shrink: 1;
        height: 100%;
        justify-content: center;
        max-width: var(--sidebar-collapsed);
        min-width: var(--sidebar-collapsed);
        overflow: hidden;
        padding: 0;
        width: var(--sidebar-collapsed);
    }

    &--open &__left-container {
        max-width: var(--sidebar-open);
        min-width: var(--sidebar-open);
        width: var(--sidebar-open);
    }

    @media (max-width: 992px) {
        &__left-container,
        &--open &__left-container {
            max-width: 140px;
            min-width: 0;
            width: auto;
        }
    }

    &__toggler {
        background-color: transparent;
        border: none;
        display: none;
        font-size: 1.3rem;

        @media (max-width: 768px) {
            display: block;
        }
    }

    &__title {
        color: $ecart-primary;
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 0;
        text-transform: capitalize;
    }

    &__navbar {
        padding-left: 45px;
        padding-right: 45px;
    }

    &__brand {
        display: block;
        flex-shrink: 0;
        height: auto;
        margin-top: -2px;
        max-height: 26px;
        object-fit: contain;
        width: 36px;
    }

    &--open &__brand {
        display: none;

        @media (max-width: 992px) {
            display: none;
        }
    }

    &__brand-logo {
        display: none;
        flex-shrink: 0;
        height: auto;
        max-height: 36px;
        max-width: 210px;
        object-fit: contain;
        padding: 0;
        width: auto;
    }

    &--open &__brand-logo {
        display: block;

        @media (max-width: 992px) {
            display: block;
        }
    }

    @media (max-width: 992px) {
        &__brand {
            display: none;
        }

        &__brand-logo {
            display: block;
            max-height: 32px;
            max-width: 160px;
        }
    }

    &__icon-button {
        align-items: center;
        background: transparent;
        border: none;
        border-radius: 8px;
        color: #495057;
        cursor: pointer;
        display: inline-flex;
        flex-shrink: 0;
        font-size: 16px;
        height: 36px;
        justify-content: center;
        padding: 0;
        position: relative;
        transition: background-color 0.15s ease;
        width: 36px;

        &:hover {
            background: $ecart-secondary-100;
            color: $general-black;
        }
    }

    &__sidebar-button {
        display: none;

        @media (max-width: 992px) {
            display: inline-flex;
        }
    }

    &__account-selector {
        width: 250px;
        display: block;

        .ui-input__container {
            height: 36px;
        }

        @media(max-width: 1025px) {
            width: auto;
            display: none;
        }
    }

    &__country-selector {
        width: 150px;

        .ui-select__container {
            height: 36px;
        }

        @media(max-width: 992px) {
            width: auto;
            display: none;
        }
    }

    &__divider {
        align-self: center;
        background: $ecart-secondary-200;
        flex-shrink: 0;
        height: 24px;
        width: 1px;
    }

    &__menu {
        align-self: stretch;
        display: flex;
    }

    &__profile-dropdown {
        align-items: center;
        align-self: stretch;
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        gap: 4px;
        margin-right: 16px;
        padding: 0;

        @media (max-width: 992px) {
            margin-right: 0;
        }

        &::after {
            display: none;
        }
    }

    &__avatar {
        align-items: center;
        align-self: center;
        display: flex;
        flex-shrink: 0;
        line-height: 0;
        margin-right: 4px;

        .avatar {
            display: flex;
            flex-shrink: 0;
        }
    }

    &__profile-meta {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;

        @media (max-width: 992px) {
            display: none;
        }
    }

    &__profile-chevron {
        color: $ecart-secondary-500;
        display: none;
        flex-shrink: 0;
        font-size: 12px;
        margin-left: 2px;

        @media (max-width: 992px) {
            display: inline-block;
        }
    }

    &__options {
        color: #495057;
    }

    &__notification-anchor {
        position: relative;
        align-self: stretch;
        display: flex;
        align-items: center;
        overflow: visible;
    }

    &__notification-dropdown {
        position: absolute;
        top: calc(100% - 4px);
        left: 50%;
        right: auto;
        transform: translateX(-50%);
        z-index: 1040;
        width: min(380px, calc(100vw - 24px));
        max-height: min(440px, 72vh);
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        background: #ffffff;
        border: 1px solid $ecart-secondary-200;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(47, 47, 51, 0.08), 0 1px 3px rgba(47, 47, 51, 0.06);
        overflow: hidden;
        color: inherit;
        box-sizing: border-box;
    }

    &__notification-dropdown-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 12px 14px;
        border-bottom: 1px solid $ecart-secondary-200;
        flex-shrink: 0;
        background: #fafbfc;
    }

    &__notification-dropdown-title {
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: $general-black;
        flex-shrink: 0;
        min-width: 0;
    }

    &__notification-dropdown-header-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
    }

    &__notification-mark-all {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: none;
        border-radius: 6px;
        background: transparent;
        color: $ecart-primary;
        cursor: pointer;
        font-size: 15px;

        &:hover {
            background: $ecart-primary-100;
            color: $ecart-primary-600;
        }

        &:focus-visible {
            outline: 2px solid $ecart-primary-300;
            outline-offset: 1px;
        }
    }

    &__notification-dropdown-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: none;
        border-radius: 4px;
        background: transparent;
        color: #6c757d;
        cursor: pointer;

        &:hover {
            background: $ecart-secondary-100;
            color: $general-black;
        }
    }

    &__notification-dropdown-footer {
        padding: 10px 12px;
        border-top: 1px solid $ecart-secondary-200;
        flex-shrink: 0;
        background: #ffffff;
    }

    &__notification-view-all {
        display: block;
        width: 100%;
        margin: 0;
        padding: 11px 14px;
        border: 1px solid $ecart-primary;
        border-radius: 6px;
        background: #ffffff;
        color: $ecart-primary;
        font-weight: 600;
        font-size: 13px;
        line-height: 1.25;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

        &:hover {
            background: $ecart-primary;
            color: #ffffff;
            border-color: $ecart-primary;
        }

        &:focus-visible {
            outline: 2px solid $ecart-primary-300;
            outline-offset: 2px;
        }
    }

    &__notification-empty {
        padding: 16px 12px;
        font-size: 13px;
        color: $ecart-secondary-500;
        text-align: center;
    }

    &__notification-badge {
        position: absolute;
        top: -2px;
        right: -2px;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: 9px;
        background: #dc3545;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 18px;
        text-align: center;
    }

    &__notification-list {
        flex: 1 1 auto;
        min-height: 120px;
        max-height: min(320px, 56vh);
        overflow-y: auto;
        overflow-x: hidden;
        padding: 10px;
        background: #fafbfc;
    }

    &__notification-row {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        width: 100%;
        text-align: left;
        border: 1px solid transparent;
        border-radius: 8px;
        padding: 0;
        margin-bottom: 8px;
        background: #ffffff;
        cursor: pointer;
        box-shadow: 0 1px 2px rgba(47, 47, 51, 0.04);

        &:last-child {
            margin-bottom: 0;
        }

        &:hover {
            border-color: $ecart-secondary-200;
            box-shadow: 0 2px 8px rgba(47, 47, 51, 0.06);
        }

        &--unread {
            border-color: rgba($ecart-primary, 0.35);
            background: $ecart-primary-100;
        }
    }

    &__notification-row-main {
        flex: 1;
        min-width: 0;
        margin: 0;
        padding: 12px 12px 14px;
        text-align: left;
        cursor: pointer;
        border-radius: 7px;
        border: none;
        background: transparent;
        font: inherit;
        color: inherit;
        appearance: none;

        &:focus-visible {
            outline: 2px solid $ecart-primary-300;
            outline-offset: 0;
        }
    }

    &__notification-mark-read-wrap {
        flex-shrink: 0;
        display: flex;
        align-items: flex-start;
        padding: 10px 8px 0 0;
    }

    &__notification-top-line {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 8px;
    }

    &__notification-top-left {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        column-gap: 6px;
        row-gap: 2px;
        padding-right: 4px;
    }

    &__notification-sep {
        color: $ecart-secondary-400;
        font-weight: 400;
        user-select: none;
    }

    &__notification-top-right {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        max-width: 46%;
    }

    &__notification-actor {
        font-size: 13px;
        font-weight: 600;
        color: $general-black;
        line-height: 1.35;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }

    &__notification-legend {
        font-size: 11px;
        font-weight: 500;
        color: $ecart-secondary-500;
        line-height: 1.35;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__notification-time {
        font-size: 11px;
        color: $ecart-secondary-500;
        line-height: 1.3;
        white-space: nowrap;
        text-align: right;
    }

    &__notification-mark-read {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: 1px solid $ecart-secondary-200;
        border-radius: 6px;
        background: #ffffff;
        color: $ecart-primary;
        cursor: pointer;
        font-size: 12px;
        flex-shrink: 0;

        &:hover {
            background: $ecart-primary;
            border-color: $ecart-primary;
            color: #ffffff;
        }

        &:focus-visible {
            outline: 2px solid $ecart-primary-300;
            outline-offset: 1px;
        }
    }

    &__notification-body {
        margin: 0;
        font-size: 13px;
        line-height: 1.45;
        color: #3d3d42;
        word-break: normal;
        overflow-wrap: anywhere;
        white-space: pre-line;
    }

    &__notification-mention {
        font-weight: 600;
        color: $ecart-primary;
    }

    &__profile-name {
        color: $general-black;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        text-align: left;
        text-overflow: ellipsis;
        overflow: hidden;
        word-break: normal;
    }

    &__profile-email {
        color: rgba($general-black, 0.7);
        font-size: 12px;
        font-weight: 500;
        line-height: 1.2;
        
        text-align: left;
    }

    &__profile-name,
    &__profile-email {
        @media (max-width: 576px) {
            width: 80px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .profile-menu-overlay {
        position: fixed;
        inset: 0;
        display: flex;
        justify-content: center;
        align-items: flex-start;
    }

    .profile-menu {
        background: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        border-radius: 4px;
        z-index: 1040;
        width: 250px;
        margin-top: 56px;
        margin-left: auto;
        position: relative;

        @media (max-width: 992px) {
            margin-left: calc(100% - 310px);
        }
    }

    .profile-menu__account {
        border-bottom: 1px solid $ecart-secondary-200;
        display: none;
        min-width: 0;
        padding: 10px 20px;

        @media (max-width: 992px) {
            display: block;
        }
    }

    .profile-menu__name {
        color: $general-black;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .profile-menu__email {
        color: $ecart-secondary-500;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.2;
        margin-top: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .profile-menu ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .profile-menu li {
        padding: 0;
    }

    .profile-menu__options {
        width: 100%;
        border: none;
        background: transparent;
        padding: 10px 20px;
        cursor: pointer;
        color: $general-black;
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
        text-align: left;

        &:hover {
            background: $ecart-secondary-100;
        }

        &--logout {
            align-items: center;
            color: $danger;
            display: flex;
            gap: 8px;

            &:hover {
                background: rgba($danger, 0.1);
                color: $danger;
            }
        }
    }
}

</style>
